import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cx } from './primitives';

/**
 * Transient confirmations. "Order cancelled", "Rates saved" - the sentence a
 * write earns, which should not become a banner the operator has to dismiss
 * before carrying on.
 *
 * Every message is passed in already translated (PRD NFR-006). Nothing here
 * carries copy of its own except through the required `closeLabel`.
 *
 * A toast is never the only report of an outcome. It is announced politely and it
 * disappears; anything a user must be able to come back to - a failure, a request
 * id - belongs in the page, not here.
 */

export type ToastTone = 'success' | 'warning' | 'danger' | 'info';

export interface Toast {
  readonly id: string;
  /** Already translated. */
  readonly message: ReactNode;
  readonly tone: ToastTone;
  /** Milliseconds on screen. `null` keeps it until dismissed. */
  readonly duration: number | null;
}

export interface ToastOptions {
  tone?: ToastTone;
  duration?: number | null;
}

export interface ToastApi {
  /** Shows a toast and returns its id. */
  show(message: ReactNode, options?: ToastOptions): string;
  dismiss(id: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * The default dwell time. Long enough to read a short sentence twice, which is
 * roughly the floor WCAG 2.2 SC 2.2.1 expects of content that vanishes on its
 * own; a `danger` toast defaults to staying until dismissed instead.
 */
const DEFAULT_DURATION_MS = 5000;

export interface ToastProviderProps {
  /** Accessible name of every toast's dismiss button. Already translated. */
  closeLabel: string;
  /** Accessible name of the toast region. Already translated. */
  regionLabel: string;
  children: ReactNode;
}

export function ToastProvider({
  closeLabel,
  regionLabel,
  children,
}: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const counter = useRef(0);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: ReactNode, options?: ToastOptions): string => {
      counter.current += 1;
      const id = `cib-toast-${counter.current}`;
      const tone = options?.tone ?? 'success';
      const duration =
        options?.duration !== undefined
          ? options.duration
          : // A failure stays. It is the one a user needs time to read and act on.
            tone === 'danger'
            ? null
            : DEFAULT_DURATION_MS;

      setToasts((current) => [...current, { id, message, tone, duration }]);

      if (duration !== null) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  // Clears anything still pending when the provider unmounts, so a timer cannot
  // fire a setState into a torn-down tree.
  const pending = timers.current;
  useEffect(() => {
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, [pending]);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        `role="status"` is polite: a confirmation interrupts nothing. The region
        is always in the DOM, empty or not - an added live region is not announced
        by every screen reader, so it has to exist before the first message does.
      */}
      <div className="cib-toasts" role="status" aria-live="polite" aria-label={regionLabel}>
        {toasts.map((toast) => (
          <div key={toast.id} className={cx('cib-toast', `cib-toast--${toast.tone}`)}>
            <p className="cib-toast__body">{toast.message}</p>
            <button
              type="button"
              className="cib-toast__close"
              onClick={() => dismiss(toast.id)}
              aria-label={closeLabel}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
                <path
                  d="M2 2l10 10M12 2L2 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Throws outside a provider rather than returning a no-op: a confirmation that
 * silently never appears is a bug that reaches production, and this one fails on
 * the first render instead.
 */
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast must be used inside a ToastProvider');
  return api;
}
